import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Link } from "react-router";
import { ArrowLeft, ShieldAlert, Users } from "lucide-react";
import { THEME } from "@/config/theme";
import {
  getAdminStatus,
  listMcpUsers,
  setMcpLockdown,
  setMcpUserBlock,
  type AdminMcpUser,
} from "@/services/adminService";

type Tab = "lockdown" | "users";

/**
 * Platform-admin page: MCP lockdown switch + per-user MCP blocks. The backend
 * 404s every route for non-admins, so this page renders a not-found style
 * message rather than leaking that an admin surface exists.
 */
export default function Administration() {
  const { getToken } = useAuth();
  // Stable ref for the same reason as McpAccess: a changing getToken identity
  // would re-fire the mount effect forever.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const [tab, setTab] = useState<Tab>("lockdown");
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null); // null = loading
  const [lockdown, setLockdown] = useState(false);
  const [users, setUsers] = useState<AdminMcpUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const status = await getAdminStatus(getTokenRef.current);
      if (!status) {
        setIsAdmin(false);
        return;
      }
      setIsAdmin(true);
      setLockdown(status.lockdown);
      setUsers(await listMcpUsers(getTokenRef.current));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function toggleLockdown() {
    setBusy(true);
    try {
      await setMcpLockdown(getTokenRef.current, !lockdown);
      setLockdown(!lockdown);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update lockdown");
    } finally {
      setBusy(false);
    }
  }

  async function toggleBlock(user: AdminMcpUser) {
    setBusy(true);
    try {
      await setMcpUserBlock(getTokenRef.current, user.userId, !user.blocked);
      setUsers((prev) =>
        prev.map((u) => (u.userId === user.userId ? { ...u, blocked: !u.blocked } : u)),
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setBusy(false);
    }
  }

  if (isAdmin === false) {
    return (
      <div className={`h-screen ${THEME.panel.text} bg-canvas-bg p-8`}>
        <p className={THEME.mcpPage.intro}>Page not found.</p>
      </div>
    );
  }

  return (
    <div className={`h-screen overflow-y-auto [color-scheme:dark] ${THEME.scrollbar.dark} ${THEME.panel.text} bg-canvas-bg`}>
      <div className="mx-auto max-w-3xl px-6 py-10">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 text-sm ${THEME.mcpPage.hint} hover:text-zinc-200`}
        >
          <ArrowLeft size={16} /> Back to canvas
        </Link>
        <h1 className={`mt-4 ${THEME.mcpPage.title}`}>Administration</h1>

        <div className="mt-6 flex gap-2" role="tablist">
          <button
            role="tab"
            aria-selected={tab === "lockdown"}
            onClick={() => setTab("lockdown")}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
              tab === "lockdown" ? THEME.adminPage.tabActive : THEME.mcpPage.hint
            }`}
          >
            <ShieldAlert size={16} /> MCP Lockdown
          </button>
          <button
            role="tab"
            aria-selected={tab === "users"}
            onClick={() => setTab("users")}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm ${
              tab === "users" ? THEME.adminPage.tabActive : THEME.mcpPage.hint
            }`}
          >
            <Users size={16} /> User Management
          </button>
        </div>

        {error && <p className={`mt-4 text-sm ${THEME.adminPage.errorText}`}>{error}</p>}

        {tab === "lockdown" && (
          <section className="mt-6">
            <p className={THEME.mcpPage.intro}>
              While lockdown is active, only admin accounts can use MCP. Every other
              user&apos;s MCP requests are rejected; the web app keeps working for everyone.
            </p>
            <button
              onClick={() => void toggleLockdown()}
              disabled={busy || isAdmin === null}
              className={`mt-4 rounded-md px-4 py-2 text-sm font-semibold ${
                lockdown ? THEME.adminPage.safeButton : THEME.adminPage.dangerButton
              } disabled:opacity-50`}
            >
              {lockdown ? "Lockdown active — disable" : "Protect from MCP DDoS"}
            </button>
          </section>
        )}

        {tab === "users" && (
          <section className="mt-6">
            <p className={THEME.mcpPage.intro}>
              Everyone holding an MCP credential (API key or OAuth token). Blocking stops
              their MCP access only.
            </p>
            <table className="mt-4 w-full text-left text-sm">
              <thead>
                <tr className={THEME.mcpPage.columnLabel}>
                  <th className="py-2 pr-4 font-medium">User</th>
                  <th className="py-2 pr-4 font-medium">Org</th>
                  <th className="py-2 pr-4 font-medium">Last used</th>
                  <th className="py-2 font-medium">MCP</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} className={`py-3 ${THEME.mcpPage.cellMuted}`}>
                      No MCP credentials issued yet.
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr key={u.userId} className={`border-t ${THEME.adminPage.rowBorder}`}>
                    <td className="py-2 pr-4 font-mono text-xs">{u.userId}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{u.orgId}</td>
                    <td className={`py-2 pr-4 ${THEME.mcpPage.cellMuted}`}>
                      {u.lastUsed ? new Date(u.lastUsed).toLocaleString() : "never"}
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => void toggleBlock(u)}
                        disabled={busy}
                        className={`rounded px-2 py-1 text-xs font-semibold ${
                          u.blocked ? THEME.adminPage.unblockChip : THEME.adminPage.blockChip
                        } disabled:opacity-50`}
                      >
                        {u.blocked ? "Unblock MCP" : "Block MCP"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
}
